import React, { useCallback, useEffect, useState } from 'react';
import { auditService } from '../services/api';
import { CONSULTA_FIELD_LABELS, PACIENTE_FIELD_LABELS, formatAuditValue } from './fieldLabels';
import './AuditLogPanel.css';

const LABELS_BY_TABLE = {
  consultas: CONSULTA_FIELD_LABELS,
  pacientes: PACIENTE_FIELD_LABELS,
};

const describeRecord = (entry) => {
  if (entry.tabla === 'consultas') {
    return `Consulta #${entry.registro_id}${entry.paciente_nombre ? ` — ${entry.paciente_nombre}` : ''}`;
  }
  if (entry.tabla === 'pacientes') {
    return `Paciente${entry.paciente_nombre ? ` ${entry.paciente_nombre}` : ` #${entry.registro_id}`}`;
  }
  return `${entry.tabla} #${entry.registro_id}`;
};

const AuditLogPanel = ({ onBack }) => {
  const [entries, setEntries] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [tablaFilter, setTablaFilter] = useState('');
  const [doctorFilter, setDoctorFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const loadLog = useCallback(async (targetPage) => {
    setLoading(true);
    setError('');
    try {
      const result = await auditService.getLog({
        page: targetPage,
        tabla: tablaFilter,
        doctor_id: doctorFilter,
        from: dateFrom,
        to: dateTo,
      });
      setEntries(result.data?.entradas || []);
      setTotal(result.data?.total || 0);
      setTotalPages(result.data?.totalPages || 1);
      setPage(result.data?.page || targetPage);
    } catch (err) {
      setEntries([]);
      setError(err.response?.data?.message || 'Error al cargar la bitácora de cambios');
    } finally {
      setLoading(false);
    }
  }, [tablaFilter, doctorFilter, dateFrom, dateTo]);

  useEffect(() => {
    loadLog(1);
  }, [loadLog]);

  useEffect(() => {
    auditService.getDoctors()
      .then((result) => setDoctors(result.data || []))
      .catch(() => setDoctors([]));
  }, []);

  const clearFilters = () => {
    setTablaFilter('');
    setDoctorFilter('');
    setDateFrom('');
    setDateTo('');
  };

  const hasFilters = tablaFilter || doctorFilter || dateFrom || dateTo;

  return (
    <div className="audit-log-container">
      <div className="breadcrumb no-print">
        <button type="button" onClick={onBack} className="breadcrumb-link">Dashboard</button>
        <span className="breadcrumb-sep">/</span>
        <span className="breadcrumb-current">Bitácora de Cambios</span>
      </div>

      <div className="audit-log-header">
        <div>
          <h2>Bitácora de Cambios</h2>
          <p className="audit-log-subtitle">
            Todas las ediciones de consultas y pacientes: quién cambió qué y cuándo.
          </p>
        </div>
        <span className="audit-log-total">{total} {total === 1 ? 'edición' : 'ediciones'}</span>
      </div>

      <div className="audit-log-filters card">
        <div className="audit-filter-group">
          <label>Registro</label>
          <select value={tablaFilter} onChange={(e) => setTablaFilter(e.target.value)}>
            <option value="">Todos</option>
            <option value="consultas">Consultas</option>
            <option value="pacientes">Pacientes</option>
          </select>
        </div>
        <div className="audit-filter-group">
          <label>Doctor</label>
          <select value={doctorFilter} onChange={(e) => setDoctorFilter(e.target.value)}>
            <option value="">Todos</option>
            {doctors.map((doctor) => (
              <option key={doctor.id} value={doctor.id}>{doctor.nombre}</option>
            ))}
          </select>
        </div>
        <div className="audit-filter-group">
          <label>Desde</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </div>
        <div className="audit-filter-group">
          <label>Hasta</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </div>
        {hasFilters && (
          <button type="button" className="btn btn-secondary btn-sm" onClick={clearFilters}>
            Limpiar filtros
          </button>
        )}
      </div>

      {error && <div className="audit-log-error">{error}</div>}

      {loading ? (
        <div className="panel-status-wrapper">
          <div className="spinner-small"></div>
          <p className="panel-status">Cargando bitácora...</p>
        </div>
      ) : entries.length === 0 ? (
        <div className="empty-state card">
          <p>{hasFilters ? 'No hay ediciones con los filtros seleccionados.' : 'Todavía no se ha editado ningún registro.'}</p>
        </div>
      ) : (
        <div className="audit-log-list">
          {entries.map((entry) => {
            const labels = LABELS_BY_TABLE[entry.tabla] || {};
            return (
              <div key={entry.id} className="audit-log-entry card">
                <div className="audit-entry-head">
                  <div className="audit-entry-record">
                    <span className={`audit-entry-badge ${entry.tabla}`}>
                      {entry.tabla === 'consultas' ? 'Consulta' : 'Paciente'}
                    </span>
                    <strong>{describeRecord(entry)}</strong>
                  </div>
                  <div className="audit-entry-meta">
                    <span className="audit-entry-doctor">Dr. {entry.doctor_nombre}</span>
                    <span className="audit-entry-date">{new Date(entry.created_at).toLocaleString('es-MX')}</span>
                  </div>
                </div>
                <ul className="audit-entry-changes">
                  {Object.entries(entry.cambios || {}).map(([field, change]) => (
                    <li key={field}>
                      <strong>{labels[field] || field}:</strong>{' '}
                      <span className="audit-value-before">{formatAuditValue(change.antes)}</span>
                      {' → '}
                      <span className="audit-value-after">{formatAuditValue(change.despues)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}

      {!loading && totalPages > 1 && (
        <div className="audit-log-pagination">
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={page <= 1}
            onClick={() => loadLog(page - 1)}
          >
            Anterior
          </button>
          <span>Página {page} de {totalPages}</span>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={page >= totalPages}
            onClick={() => loadLog(page + 1)}
          >
            Siguiente
          </button>
        </div>
      )}
    </div>
  );
};

export default AuditLogPanel;
